import React, { useState, useRef, useCallback } from 'react';
import { X, Bed, Bath, Square, MapPin, Heart, Share2, ChevronLeft, ChevronRight, Play, Maximize2, Minimize2, Globe } from 'lucide-react';
import cr3ateBird from '../assets/logo-small.gif';
import cr3ateWordmark from '../assets/cr3ate-ai-logo.svg';
import { FLOOR_SVG, FLOOR_LABEL, type KimbarkFloor } from '../data/kimbarkRooms';

const PHOTOS = [
  'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&q=80',
  'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80',
  'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=1200&q=80',
  'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80',
  'https://images.unsplash.com/photo-1600573472591-ee6b68d14c68?w=1200&q=80',
];

const RealEstateDemo: React.FC = () => {
  const [photoIdx, setPhotoIdx] = useState(0);
  const [galleryTab, setGalleryTab] = useState<'photos' | 'floormap'>('photos');
  const [activeFloor, setActiveFloor] = useState<KimbarkFloor>('ground');
  const [tourOpen, setTourOpen] = useState(false);
  const [neighbourhoodOpen, setNeighbourhoodOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [saved, setSaved] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const neighbourhoodModalRef = useRef<HTMLDivElement>(null);

  const prev = () => setPhotoIdx(i => (i - 1 + PHOTOS.length) % PHOTOS.length);
  const next = () => setPhotoIdx(i => (i + 1) % PHOTOS.length);

  const toggleFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await modalRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const closeTour = useCallback(async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    setIsFullscreen(false);
    setTourOpen(false);
  }, []);

  const toggleNeighbourhoodFullscreen = useCallback(async () => {
    if (!document.fullscreenElement) {
      await neighbourhoodModalRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      await document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const closeNeighbourhood = useCallback(async () => {
    if (document.fullscreenElement) await document.exitFullscreen();
    setIsFullscreen(false);
    setNeighbourhoodOpen(false);
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">

      {/* ── Redfin Header ─────────────────────────────────────────────── */}
      <header className="sticky top-0 z-40 bg-[#CC0000] shadow-md">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg width="28" height="28" viewBox="0 0 40 40" fill="none">
              <circle cx="20" cy="20" r="20" fill="white" fillOpacity="0.15" />
              <path d="M20 8C13.4 8 8 13.4 8 20s5.4 12 12 12 12-5.4 12-12S26.6 8 20 8zm0 4c1.7 0 3.2.6 4.4 1.6L12.6 24.4C11.6 23.2 11 21.7 11 20c0-5 4-9 9-9zm0 18c-1.7 0-3.2-.6-4.4-1.6l11.8-11.8c1 1.2 1.6 2.7 1.6 4.4 0 5-4 9-9 9z" fill="white" />
            </svg>
            <span className="text-white font-bold text-xl tracking-tight">redfin</span>
          </div>
          <nav className="hidden md:flex items-center gap-6 text-white/90 text-sm font-medium">
            <a href="#" className="hover:text-white">Buy</a>
            <a href="#" className="hover:text-white">Sell</a>
            <a href="#" className="hover:text-white">Rent</a>
            <a href="#" className="hover:text-white">Mortgage</a>
          </nav>
          <div className="flex items-center gap-3">
            <button className="text-white/80 hover:text-white text-sm">Log In</button>
            <button className="bg-white text-[#CC0000] font-semibold text-sm px-4 py-1.5 rounded-full hover:bg-red-50 transition-colors">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">

        {/* ── Breadcrumb ─────────────────────────────────────────────── */}
        <div className="text-sm text-gray-500 mb-4 flex items-center gap-1">
          <a href="#" className="hover:underline text-[#CC0000]">Chicago</a>
          <span>›</span>
          <a href="#" className="hover:underline text-[#CC0000]">Lincoln Park</a>
          <span>›</span>
          <span className="text-gray-700">1322 N Kimbark Ave</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">

          {/* ── Left Column ────────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">

            {/* Photo / Floor Plan gallery — single frame, toggled by pills */}
            <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-[16/9] mb-3 group">

              {galleryTab === 'photos' ? (
                <>
                  <img
                    src={PHOTOS[photoIdx]}
                    alt="Property"
                    className="w-full h-full object-cover transition-opacity duration-300"
                  />
                  <button
                    onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft size={20} className="text-gray-700" />
                  </button>
                  <button
                    onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight size={20} className="text-gray-700" />
                  </button>
                  <div className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2.5 py-1 rounded-full">
                    {photoIdx + 1} / {PHOTOS.length}
                  </div>
                </>
              ) : (
                <>
                  <div className="w-full h-full bg-white flex items-center justify-center p-8">
                    <img
                      src={FLOOR_SVG[activeFloor]}
                      alt={`${FLOOR_LABEL[activeFloor]} floor plan`}
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  {/* Floor level tabs inside image */}
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1 bg-black/50 backdrop-blur-sm rounded-full p-1">
                    {(['basement', 'ground', 'upper'] as KimbarkFloor[]).map(floor => (
                      <button
                        key={floor}
                        onClick={() => setActiveFloor(floor)}
                        className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${activeFloor === floor ? 'bg-white text-gray-900' : 'text-white/70 hover:text-white'}`}
                      >
                        {FLOOR_LABEL[floor]}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {/* Bottom-left action pills — always visible */}
              <div className="absolute bottom-3 left-3 flex gap-2">
                <button
                  onClick={() => setTourOpen(true)}
                  className="flex items-center gap-1.5 bg-white text-gray-800 font-semibold text-sm px-3.5 py-2 rounded-full shadow-lg hover:bg-gray-50 transition-colors border border-gray-200"
                >
                  <Play size={13} className="text-[#CC0000] fill-[#CC0000]" />
                  3D Tour
                  <span className="text-gray-300 mx-0.5">·</span>
                  <img src={cr3ateBird} alt="" className="h-4 w-auto" />
                </button>
                <button
                  onClick={() => setGalleryTab(t => t === 'floormap' ? 'photos' : 'floormap')}
                  className={`flex items-center gap-1.5 text-sm font-semibold px-3.5 py-2 rounded-full shadow-lg transition-colors border ${galleryTab === 'floormap' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'}`}
                >
                  <svg viewBox="0 0 14 14" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.6">
                    <rect x="0.7" y="0.7" width="5" height="5" rx="0.4" />
                    <rect x="8.3" y="0.7" width="5" height="3" rx="0.4" />
                    <rect x="0.7" y="8.3" width="3.5" height="5" rx="0.4" />
                    <rect x="6.3" y="6.3" width="7" height="7" rx="0.4" />
                  </svg>
                  Floor Plan
                </button>
              </div>
            </div>

            {/* Thumbnail strip — photos mode only */}
            {galleryTab === 'photos' && (
              <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
                {PHOTOS.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => setPhotoIdx(i)}
                    className={`flex-none w-20 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === photoIdx ? 'border-[#CC0000]' : 'border-transparent'}`}
                  >
                    <img src={p} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}

            {/* Address + price */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">$1,295,000</h1>
                <div className="flex items-center gap-1 text-gray-600 mt-1">
                  <MapPin size={15} className="text-[#CC0000] flex-none" />
                  <span className="text-base">1322 N Kimbark Ave, Chicago, IL 60614</span>
                </div>
              </div>
              <div className="flex gap-2 mt-1">
                <button
                  onClick={() => setSaved(s => !s)}
                  className="p-2 rounded-full border border-gray-200 hover:bg-red-50 transition-colors"
                >
                  <Heart size={18} className={saved ? 'fill-[#CC0000] text-[#CC0000]' : 'text-gray-400'} />
                </button>
                <button className="p-2 rounded-full border border-gray-200 hover:bg-gray-50 transition-colors">
                  <Share2 size={18} className="text-gray-400" />
                </button>
              </div>
            </div>

            {/* Stats bar */}
            <div className="flex gap-6 py-4 border-y border-gray-100 mb-6">
              <div className="flex items-center gap-2">
                <Bed size={18} className="text-gray-500" />
                <span className="font-semibold">4</span>
                <span className="text-gray-500 text-sm">beds</span>
              </div>
              <div className="flex items-center gap-2">
                <Bath size={18} className="text-gray-500" />
                <span className="font-semibold">3.5</span>
                <span className="text-gray-500 text-sm">baths</span>
              </div>
              <div className="flex items-center gap-2">
                <Square size={18} className="text-gray-500" />
                <span className="font-semibold">2,847</span>
                <span className="text-gray-500 text-sm">sq ft</span>
              </div>
            </div>

            {/* Status badges */}
            <div className="flex gap-2 mb-6">
              <span className="bg-green-100 text-green-700 text-xs font-semibold px-3 py-1 rounded-full">Active</span>
              <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full">New Construction</span>
            </div>

            {/* Description */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3">About this home</h2>
              <p className="text-gray-600 leading-relaxed text-sm">
                Stunning new construction in the heart of Lincoln Park. This exceptional 4-bedroom, 3.5-bath residence
                showcases masterful craftsmanship with soaring 10-ft ceilings, wide-plank white oak floors, and an
                open-concept layout designed for modern living. The chef's kitchen features custom cabinetry, quartz
                countertops, and premium appliances. The primary suite offers a spa-inspired bath and walk-in closet.
                Enjoy the private rooftop deck with skyline views, a finished basement, and a 2-car garage. Steps from
                Oz Park, top-rated restaurants, and Lincoln Park's best shopping.
              </p>
            </div>

            {/* Neighbourhood View */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3">Neighbourhood</h2>
              <div
                className="relative rounded-xl overflow-hidden cursor-pointer group"
                onClick={() => setNeighbourhoodOpen(true)}
              >
                <img
                  src="https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1200&q=80"
                  alt="Neighbourhood aerial"
                  className="w-full h-52 object-cover group-hover:scale-[1.02] transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/30 group-hover:bg-black/20 transition-colors" />
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                  <div className="bg-white/90 rounded-full p-3 shadow-lg">
                    <Globe size={22} className="text-[#CC0000]" />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold text-sm drop-shadow">View 3D Neighbourhood Tour</span>
                    <img src={cr3ateBird} alt="" className="h-4 w-auto opacity-90" />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-2">Interactive 3D aerial reconstruction of the Lincoln Park neighbourhood · Walk Score 95</p>
            </div>

            {/* Facts grid */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-3">Property details</h2>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                {[
                  ['Type', 'Single Family'],
                  ['Year built', '2026'],
                  ['Lot size', '3,125 sq ft'],
                  ['Garage', '2-car attached'],
                  ['Basement', 'Finished'],
                  ['HOA fee', 'None'],
                  ['Taxes', '$8,240 / yr (est.)'],
                  ['MLS #', 'MLS-11928340'],
                ].map(([label, val]) => (
                  <div key={label} className="flex justify-between py-1.5 border-b border-gray-100">
                    <span className="text-gray-500">{label}</span>
                    <span className="font-medium text-gray-800">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right Column — CTA card ─────────────────────────────── */}
          <div className="lg:w-80 flex-none">
            <div className="sticky top-20 rounded-xl border border-gray-200 shadow-lg overflow-hidden">
              {/* Tour CTAs */}
              <div className="p-5 flex flex-col gap-3 border-b border-gray-100">
                <button
                  onClick={() => setTourOpen(true)}
                  className="relative w-full flex items-center justify-center gap-2 bg-[#CC0000] hover:bg-[#aa0000] text-white font-semibold py-3 px-4 rounded-lg transition-colors shadow-sm"
                >
                  <Play size={16} className="fill-white" />
                  View 3D Tour
                  <img src={cr3ateBird} alt="" className="absolute right-3 h-4 w-auto opacity-80" />
                </button>
                <button
                  onClick={() => setNeighbourhoodOpen(true)}
                  className="relative w-full flex items-center justify-center gap-2 border-2 border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:border-gray-400 hover:bg-gray-50 transition-colors"
                >
                  <Globe size={16} className="text-gray-500" />
                  Neighbourhood View
                  <img src={cr3ateBird} alt="" className="absolute right-3 h-4 w-auto opacity-50" />
                </button>
                <button className="w-full border-2 border-[#CC0000] text-[#CC0000] font-semibold py-3 px-4 rounded-lg hover:bg-red-50 transition-colors">
                  Schedule a Tour
                </button>
              </div>

              <div className="p-5">
                <p className="text-sm text-gray-500 mb-3">Contact listing agent</p>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#CC0000] flex items-center justify-center text-white font-bold text-sm">SA</div>
                  <div>
                    <p className="text-sm font-semibold">Sarah Anderson</p>
                    <p className="text-xs text-gray-400">Redfin Agent · 4.9 ★</p>
                  </div>
                </div>
                <button className="w-full border border-gray-200 text-gray-700 text-sm font-medium py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors">
                  Send Message
                </button>
                <p className="text-center text-xs text-gray-400 mt-3">Listed by Redfin · MLS #11928340</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── 3D Tour iframe modal ───────────────────────────────────── */}
      {tourOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="relative w-full h-full max-w-7xl max-h-[95vh] mx-4 my-4 rounded-2xl overflow-hidden shadow-2xl bg-black"
          >
            {/* Header bar */}
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
              {/* cr3ate logo left */}
              <div className="flex items-center gap-2 pointer-events-none">
                <img src={cr3ateBird} alt="" className="h-7 w-auto" />
                <img src={cr3ateWordmark} alt="cr3ate" className="h-3.5 w-auto opacity-80" />
                <span className="text-white/50 text-xs ml-1">· 1322 N Kimbark Ave</span>
              </div>
              {/* Controls right */}
              <div className="flex items-center gap-1 pointer-events-auto">
                <button
                  onClick={toggleFullscreen}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
                >
                  {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button
                  onClick={closeTour}
                  title="Close"
                  className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <iframe
              src="/multi-viewer?scene=construction"
              title="3D Tour"
              className="w-full h-full border-0"
              allow="fullscreen"
            />
          </div>
        </div>
      )}
      {/* ── Neighbourhood iframe modal ─────────────────────────────── */}
      {neighbourhoodOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <div
            ref={neighbourhoodModalRef}
            className="relative w-full h-full max-w-7xl max-h-[95vh] mx-4 my-4 rounded-2xl overflow-hidden shadow-2xl bg-black"
          >
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 bg-gradient-to-b from-black/70 to-transparent pointer-events-none">
              <div className="flex items-center gap-2 pointer-events-none">
                <img src={cr3ateBird} alt="" className="h-7 w-auto" />
                <img src={cr3ateWordmark} alt="cr3ate" className="h-3.5 w-auto opacity-80" />
                <span className="text-white/50 text-xs ml-1">· Lincoln Park Neighbourhood View</span>
              </div>
              <div className="flex items-center gap-1 pointer-events-auto">
                <button
                  onClick={toggleNeighbourhoodFullscreen}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
                >
                  {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                </button>
                <button
                  onClick={closeNeighbourhood}
                  title="Close"
                  className="text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-1.5 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
            <iframe
              src="/viewer?scene=drone_construction_site"
              title="Neighbourhood View"
              className="w-full h-full border-0"
              allow="fullscreen"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RealEstateDemo;
